import every from 'lodash/every'

export const usePermissionCheck = ({
  requiredUserPermissions,
  requiredCompanyPermissions,
  mustBeCompanyAdmin,
  mustBeServiceAdmin,
  mustBeServiceUser,
  isAssistMode: assistMode,
}: {
  requiredUserPermissions?: UserPermission | UserPermission[]
  requiredCompanyPermissions?: CompanyPermission | CompanyPermission[]
  mustBeCompanyAdmin?: boolean
  mustBeServiceAdmin?: boolean
  mustBeServiceUser?: boolean
  isAssistMode?: boolean
}) => {
  const { accountRoleInfo, accountInfo, isAdmin, isAssistMode } = useAuth()

  const currentCompanyPermissions = computed<CompanyPermission>(() => {
    const {
      isEnableJobEdit,
      isEnableScout,
      isEnableUserRead,
      isNotifySearchConditionMatch,
    } = accountInfo.value?.plan || {}
    const { isUsingAgent } = accountInfo.value?.agent || {}
    return {
      isEnableJobEdit,
      isEnableScout,
      isEnableUserRead,
      isNotifySearchConditionMatch,
      isUsingAgent,
    }
  })

  const currentUserPermissions = computed(() => {
    return accountRoleInfo.value?.permissions
  })

  const checkCompanyPermission = ({
    requiredCompanyPermissions,
    currentCompanyPermissions,
  }: {
    requiredCompanyPermissions: CompanyPermission[]
    currentCompanyPermissions: GetAccount200ResponsePlan
  }): boolean => {
    return requiredCompanyPermissions.some((requiredCompanyPermission) =>
      Object.keys(requiredCompanyPermission).every((key) => {
        return (
          requiredCompanyPermission[key as keyof CompanyPermission] ===
          currentCompanyPermissions[key as keyof GetAccount200ResponsePlan]
        )
      }),
    )
  }

  const checkUserPermission = ({
    requiredUserPermissions,
    currentUserPermissions,
  }: {
    requiredUserPermissions: UserPermission[]
    currentUserPermissions: GetCompanyRoleSettings200ResponsePermissionsInner[]
  }): boolean => {
    return requiredUserPermissions.some((requiredUserPermission) =>
      every(
        requiredUserPermission,
        (requiredUserPermissionFunction, permissionCode) => {
          if (!requiredUserPermissionFunction) {
            return true
          }
          const currentPermission = currentUserPermissions.find(
            (p) => p.permissionCode === permissionCode,
          )
          return requiredUserPermissionFunction.every(
            (requiredFunction) => currentPermission?.[requiredFunction],
          )
        },
      ),
    )
  }

  const companyPermitted = computed(() => {
    if (!requiredCompanyPermissions) {
      return true
    }
    return checkCompanyPermission({
      requiredCompanyPermissions: Array.isArray(requiredCompanyPermissions)
        ? requiredCompanyPermissions
        : [requiredCompanyPermissions],
      currentCompanyPermissions: currentCompanyPermissions.value || {},
    })
  })

  const userPermitted = computed(() => {
    if (!requiredUserPermissions) {
      return true
    }
    return checkUserPermission({
      requiredUserPermissions: Array.isArray(requiredUserPermissions)
        ? requiredUserPermissions
        : [requiredUserPermissions],
      currentUserPermissions: currentUserPermissions.value || [],
    })
  })

  const companyAdminPermitted = computed(() => {
    return mustBeCompanyAdmin ? accountRoleInfo.value?.isAdmin : true
  })

  const serviceAdminPermitted = computed(() => {
    return mustBeServiceAdmin ? isAdmin.value : true
  })

  const serviceUserPermitted = computed(() => {
    return mustBeServiceUser ? !isAdmin.value : true
  })

  const assistModePermitted = computed(() => {
    return assistMode !== undefined ? isAssistMode.value === assistMode : true
  })

  const permitted = computed(() => {
    return (
      companyPermitted.value &&
      userPermitted.value &&
      companyAdminPermitted.value &&
      serviceAdminPermitted.value &&
      serviceUserPermitted.value &&
      assistModePermitted.value
    )
  })

  return {
    permitted,
  }
}
